/**
 * Art-bible-compliant color palette for all HUD elements.
 * Reference: docs/art/art-bible.md — 24-color master palette.
 */

// Master palette indexed colors (as hex numbers for Phaser)
export const PAL_BLACK = 0x0a0a0a;       // 0 — Black
export const PAL_DARK_GRAY = 0x2b2b3d;   // 1
export const PAL_MID_GRAY = 0x4e4e6e;    // 2 — Mid Gray
export const PAL_LIGHT_GRAY = 0x8b8ba8;  // 3
export const PAL_OFF_WHITE = 0xd4d4e4;   // 4
export const PAL_RED = 0xb82e3c;         // 11 — health bar fill
export const PAL_RED_DARK = 0x6e1a26;    // 10 — Dark Red — low health pulse
export const PAL_GREEN = 0x7ec86a;       // 16 — Light Green — stamina / healing
export const PAL_BLUE = 0x3a5a9a;        // 18 — mana bar fill
export const PAL_YELLOW = 0xe8c44a;      // 13 — crit damage numbers

// HUD-specific derived usage
export const HUD_BG = PAL_DARK_GRAY;
export const HUD_BORDER = PAL_BLACK;
export const HUD_TEXT_PRIMARY = PAL_OFF_WHITE;
export const HUD_TEXT_SECONDARY = PAL_LIGHT_GRAY;

export const HEALTH_BAR_FILL = PAL_RED;
export const HEALTH_BAR_BG = PAL_MID_GRAY;
export const MANA_BAR_FILL = PAL_BLUE;
export const MANA_BAR_BG = PAL_MID_GRAY;

export const COOLDOWN_READY = PAL_GREEN;
export const COOLDOWN_ACTIVE = PAL_MID_GRAY;

export const DAMAGE_COLOR_NORMAL = PAL_OFF_WHITE;
export const DAMAGE_COLOR_CRIT = PAL_YELLOW;

// Loot rarity colors
export const LOOT_COMMON = PAL_LIGHT_GRAY;
export const LOOT_UNCOMMON = PAL_GREEN;
export const LOOT_RARE = PAL_BLUE;
export const LOOT_EPIC = 0x6a3a8a;          // 21 — Purple
export const LOOT_LEGENDARY = 0xd46a2a;     // 12 — Orange — legendary tier
export const LOOT_GLOW = PAL_YELLOW;        // pickup indicator glow

// Extraction UI
export const EXTRACTION_BAR_FILL = PAL_GREEN;
export const EXTRACTION_BAR_BG = PAL_MID_GRAY;

// Results screen
export const RESULTS_BG = PAL_BLACK;
export const RESULTS_TITLE = PAL_YELLOW;
export const RESULTS_TEXT = PAL_OFF_WHITE;
export const RESULTS_LABEL = PAL_LIGHT_GRAY;

// Inventory & equipment UI
export const INVENTORY_BG = PAL_DARK_GRAY;
export const INVENTORY_BORDER = PAL_BLACK;
export const INVENTORY_SLOT_BG = PAL_MID_GRAY;
export const INVENTORY_SLOT_EMPTY = 0x2b2b3d; // 1 — Dark Gray
export const INVENTORY_SLOT_HIGHLIGHT = PAL_YELLOW;
export const EQUIP_SLOT_WEAPON = 0xa8614a;   // 8 — Tan — warm bronze
export const EQUIP_SLOT_ARMOR = 0x3a5a9a;    // 18 — Blue — steel
export const EQUIP_SLOT_ACCESSORY = 0x6a3a8a; // 21 — Purple — amethyst

// Pause & settings menu
export const PAUSE_OVERLAY_BG = PAL_BLACK;
export const PAUSE_PANEL_BG = PAL_DARK_GRAY;
export const PAUSE_PANEL_BORDER = PAL_BLACK;
export const PAUSE_TITLE = PAL_YELLOW;
export const PAUSE_BUTTON_BG = PAL_MID_GRAY;
export const PAUSE_BUTTON_HOVER = 0x8b8ba8;     // 3 — Light Gray — hover state
export const PAUSE_BUTTON_TEXT = PAL_OFF_WHITE;
export const SETTINGS_SLIDER_BG = PAL_MID_GRAY;
export const SETTINGS_SLIDER_FILL = PAL_GREEN;
export const SETTINGS_LABEL = PAL_OFF_WHITE;
export const SETTINGS_VALUE = PAL_YELLOW;
export const SETTINGS_SECTION_HEADER = PAL_YELLOW;
export const SETTINGS_KEY_BG = 0x2b2b3d;        // 1 — Dark Gray — key badge bg
export const SETTINGS_KEY_TEXT = PAL_LIGHT_GRAY;

// Challenge UI
export const CHALLENGE_BG = PAL_DARK_GRAY;
export const CHALLENGE_BORDER = PAL_BLACK;
export const CHALLENGE_PROGRESS_FILL = PAL_GREEN;
export const CHALLENGE_PROGRESS_BG = PAL_MID_GRAY;
export const CHALLENGE_COMPLETE = PAL_YELLOW;
export const CHALLENGE_CLAIMED = PAL_LIGHT_GRAY;
export const CHALLENGE_TIMER = 0xd46a2a;            // 12 — Orange — urgency

// ── C5 "Indigo & Ink" materials ──
// Phaser-side mapping of the CSS tokens in docs/design/ui-c5-indigo-ink.md
// (single DOM source: src/ui/c5/c5-tokens.css). Phaser-drawn surfaces that
// cannot be DOM yet — dialogue slips, the journal spread, hearth/board paper —
// read these instead of the gray HUD_* constants.

// Console (the permanent apparatus — dark, quiet, never decorates itself)
export const C5_BAND = 0x0c0e18;        // --c5-band — letterbox band / veil base
export const C5_BAND_LINE = 0x23263c;   // --c5-band-line — hairline borders
export const C5_SOCKET = 0x1a1c2e;      // --c5-socket — socket well
export const C5_SOCKET_RIM = 0x3a3d5c;  // --c5-socket-rim — socket border, ticks
export const C5_TEXT_DIM = 0x5e5b78;    // --c5-text-dim — secondary band text
export const C5_TEXT = 0x9a97b4;        // --c5-text — primary band text
export const C5_TEXT_HI = 0xc9c5dd;     // --c5-text-hi — emphasized band text

// Paper (anything a human or the world authored)
export const C5_PAPER_HI = 0xf2e8cc;    // --c5-paper-hi — lit top edge of a slip
export const C5_PAPER = 0xe0d2ac;       // --c5-paper — slip body
export const C5_PAPER_DK = 0xbfa87c;    // --c5-paper-dk — shaded fold / deckle edge
export const C5_INK = 0x4a3a26;         // --c5-ink — text on paper
export const C5_INK_DIM = 0x8a734a;     // --c5-ink-dim — secondary ink, brass

// Authored marks
export const C5_HANKO = 0xa83240;       // --c5-hanko — stamps, wax seals, speaker names
export const C5_VITAL_INK = 0xb83a48;   // --c5-vital-ink — vitality brush stroke
export const C5_BREATH_INK = 0x4a6aa0;  // --c5-breath-ink — breath brush stroke

// Derived surface roles (so components never invent their own hex)
export const C5_SLIP_FILL = C5_PAPER;
export const C5_SLIP_FILL_HI = C5_PAPER_HI;
export const C5_SLIP_EDGE = C5_PAPER_DK;
export const C5_SLIP_SHADOW = PAL_BLACK;      // soft drop shadow under paper
export const C5_SLIP_SHADOW_ALPHA = 0.34;
export const C5_VEIL = C5_BAND;               // indigo veil behind paper spreads
export const C5_VEIL_ALPHA = 0.82;
export const C5_TEXT_ON_PAPER = C5_INK;
export const C5_TEXT_ON_PAPER_DIM = C5_INK_DIM;

// ── Shared lookup maps ──

import { EquipmentSlot } from '@game/shared';

/** Rarity → color mapping for loot/inventory/results UI */
export const RARITY_COLORS: Record<string, number> = {
  common: LOOT_COMMON,
  uncommon: LOOT_UNCOMMON,
  rare: LOOT_RARE,
  epic: LOOT_EPIC,
  legendary: LOOT_LEGENDARY,
};

/** Equipment slot → color mapping for inventory/HUD */
export const SLOT_COLORS: Record<string, number> = {
  [EquipmentSlot.Weapon]: EQUIP_SLOT_WEAPON,
  [EquipmentSlot.Armor]: EQUIP_SLOT_ARMOR,
  [EquipmentSlot.Accessory]: EQUIP_SLOT_ACCESSORY,
};

// CSS color strings for Phaser Text objects
export function hexToCSS(hex: number): string {
  return '#' + hex.toString(16).padStart(6, '0');
}
